import {
  Concurrency,
  ConflictDetection,
  ConstraintKey,
  EditorType,
  ImplicitFieldStrategy,
  SubmitMode,
  ValueSource,
  ValueType,
  WriteStrategy,
} from './constants.js';
import type { BulkUpdateAttributeDefinition } from './types.js';

// Union of BE BULK_UPDATE_EDITABLE_ATTRIBUTES, FE displaying.ts bulkUpdate configs,
// and FE BULK_UPDATE_EXTRA_ACTIONS — the single source of truth.
//
// ITERABLE SYNC ALIGNMENT:
// When iterableSync: true, bulk edits trigger Iterable user profile updates.
// Client fields synced: firstName, lastName, frequentFlyer, marketingOptOut
// Address fields synced: address1, address2, businessName, city, state, zip,
//   clientActivatedMembership (serviceLevel), tosAccepted, creditCardProvided,
//   brandId, labels (addressLabels)
// See: apps/backend/assets/lambdas/src/business-layer/external-services/iterable/sync-iterable.ts

const BULK_UPDATE_REGISTRY: BulkUpdateAttributeDefinition[] = [
  // ─── Conversation ──────────────────────────────────────────────
  {
    entityName: 'Conversation',
    attribute: 'conversationUrgencyId',
    label: 'Urgency',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getEnabledConversationUrgencies',
      icon: 'flag',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRef,
      constraintKey: ConstraintKey.UrgencyAllowlist,
    },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'conversationStatusId',
    label: 'Status',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Status,
      optionsSource: 'getConversationStatus',
      icon: 'circle-dot',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRef,
      accessRuleKey: ['AccessToUpdateAllConversationStatuses'],
    },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
      metadata: {
        fields: ['snoozeUntil'],
        joiShape: { snoozeUntil: 'date' },
      },
      implicitWriteFields: {
        snoozeUntil: ImplicitFieldStrategy.SetFromMetadata,
        conversationStatusUpdatedAt: ImplicitFieldStrategy.SetTimestamp,
      },
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'subject',
    label: 'Subject',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'file-text' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'type',
    label: 'Type',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getConversationType',
      icon: 'tag',
    },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'requesterId',
    label: 'Requester',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.EntitySearch,
      optionsSource: 'getConversationParticipantOptions',
      icon: 'user',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'addressId',
    label: 'Site Address',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.EntitySearch,
      optionsSource: 'getAddress',
      icon: 'map-pin',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.AssociationReplace,
      concurrency: Concurrency.Parallel,
      snapshotAttributes: ['associatedAddresses.addressId'],
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'conversationAssigneeGroupId',
    label: 'Group',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getEnabledConversationAssigneeGroups',
      icon: 'users',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRef,
      constraintKey: ConstraintKey.AssigneeGroupAllowlist,
    },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'conversationAssigneeId',
    label: 'Assignee',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getPlatformUser',
      icon: 'user-check',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'addLabels',
    label: 'Add labels',
    actionLabel: 'Add labels',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.MultiSelect,
      optionsSource: 'getVisibleLabels',
      icon: 'tags',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRefArray,
      constraintKey: ConstraintKey.VisibleLabelsForEntity,
    },
    execution: {
      writeStrategy: WriteStrategy.LabelMutation,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'removeLabels',
    label: 'Remove labels',
    actionLabel: 'Remove labels',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.MultiSelect,
      optionsSource: 'getVisibleLabels',
      icon: 'tag-x',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRefArray,
      constraintKey: ConstraintKey.VisibleLabelsForEntity,
    },
    execution: {
      writeStrategy: WriteStrategy.LabelMutation,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },

  // ─── Address (Site) ────────────────────────────────────────────
  {
    entityName: 'Address',
    attribute: 'address1',
    label: 'Address1',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'map-pin' },
    validation: {
      required: true,
      valueType: ValueType.String,
      constraintKey: ConstraintKey.AddressUniqueness,
    },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Serial,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'address2',
    label: 'Address2',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'map-pin' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'businessName',
    label: 'Business Name',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'building' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'city',
    label: 'City',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'building-2' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'state',
    label: 'State',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Select, icon: 'landmark' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'zip',
    label: 'Zip/Postal Code',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'map-pin' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'brandId',
    label: 'Client Segment',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getBrand',
      icon: 'tag',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'clientActivatedMembership',
    label: 'Service Level',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Select, icon: 'star' },
    validation: {
      required: true,
      valueType: ValueType.String,
      constraintKey: ConstraintKey.ServiceLevelBrand,
    },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
      metadata: {
        fields: ['platformSlaId'],
        joiShape: { platformSlaId: 'string' },
      },
      implicitWriteFields: {
        platformSlaId: ImplicitFieldStrategy.SetFromMetadata,
      },
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'tosAccepted',
    label: 'TOS',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Boolean,
      optionsSource: 'getBooleanOptions',
      icon: 'scroll-text',
    },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'creditCardProvided',
    label: 'Membership Registered',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Boolean,
      optionsSource: 'getBooleanOptions',
      icon: 'wallet',
    },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'primaryClientId',
    label: 'Primary Client',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.EntitySearch,
      optionsSource: 'getClient',
      icon: 'user',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.AssociationEnsureThenUpdate,
      concurrency: Concurrency.Parallel,
      preWriteHook: 'ensurePrimaryClientAssociation',
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'addClientId',
    label: 'Add Client',
    actionLabel: 'Add client to sites',
    isMultitenancyAllowed: false,
    editor: {
      type: EditorType.EntitySearch,
      optionsSource: 'getClient',
      icon: 'user-plus',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRef,
      constraintKey: ConstraintKey.ActiveClientInPartner,
    },
    execution: {
      writeStrategy: WriteStrategy.AssociationAdd,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.None },
  },
  {
    entityName: 'Address',
    attribute: 'removeClientId',
    label: 'Remove Client',
    actionLabel: 'Remove client from sites',
    isMultitenancyAllowed: false,
    // The client id comes from the hosting page (the client's Sites tab), so this action renders no
    // editor and is offered only there.
    editor: {
      type: EditorType.Text,
      icon: 'user-minus',
      valueSource: ValueSource.HostContext,
    },
    validation: {
      required: true,
      valueType: ValueType.IdRef,
      constraintKey: ConstraintKey.ActiveClientInPartner,
    },
    execution: {
      writeStrategy: WriteStrategy.AssociationRemove,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.None },
    confirmation: {
      title: 'Remove client from selected sites?',
      getDescription: (selectedCount, totalCount) =>
        `You are about to remove this client from ${selectedCount} of ${totalCount} selected sites. The client record and all conversations are kept — only the client-to-site link is removed. Where this client is the site's primary client, the primary client is cleared. This can be undone for a limited time.`,
      warning: 'Undo is available only for a limited time and only for this job.',
      cancelButtonTitle: 'Cancel',
      confirmButtonTitle: 'Remove',
      confirmButtonColor: 'error',
    },
  },
  {
    entityName: 'Address',
    attribute: 'addLabels',
    label: 'Add labels',
    actionLabel: 'Add labels',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.MultiSelect,
      optionsSource: 'getVisibleLabels',
      icon: 'tags',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRefArray,
      constraintKey: ConstraintKey.VisibleLabelsForEntity,
    },
    execution: {
      writeStrategy: WriteStrategy.LabelMutation,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Address',
    attribute: 'removeLabels',
    label: 'Remove labels',
    actionLabel: 'Remove labels',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.MultiSelect,
      optionsSource: 'getVisibleLabels',
      icon: 'tag-x',
    },
    validation: {
      required: true,
      valueType: ValueType.IdRefArray,
      constraintKey: ConstraintKey.VisibleLabelsForEntity,
    },
    execution: {
      writeStrategy: WriteStrategy.LabelMutation,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },

  // ─── Client ────────────────────────────────────────────────────
  {
    entityName: 'Client',
    attribute: 'firstName',
    label: 'First Name',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'user' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Client',
    attribute: 'lastName',
    label: 'Last Name',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'user' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Client',
    attribute: 'frequentFlyer',
    label: 'Frequent Flyer',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Boolean,
      optionsSource: 'getBooleanOptions',
      icon: 'plane',
    },
    validation: { required: false, valueType: ValueType.Boolean },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Client',
    attribute: 'marketingOptOut',
    label: 'Marketing Opt Out',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Boolean,
      optionsSource: 'getBooleanOptions',
      icon: 'bell-off',
    },
    validation: { required: false, valueType: ValueType.Boolean },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Client',
    attribute: 'addressId',
    label: 'Associate with Site',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.EntitySearch,
      optionsSource: 'getAddress',
      icon: 'map-pin',
      submitMode: SubmitMode.AssociateClientsWithAddress,
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: true, rteInvalidation: true },
    undo: { supported: false, conflictDetection: ConflictDetection.None },
  },

  // ─── Project ───────────────────────────────────────────────────
  {
    entityName: 'Project',
    attribute: 'name',
    label: 'Project Name',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'file-pen' },
    validation: { required: false, valueType: ValueType.String },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Project',
    attribute: 'projectStageId',
    label: 'Project Stage',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getProjectStage',
      icon: 'layers',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Project',
    attribute: 'projectLostReasonId',
    label: 'Lost Reason',
    isMultitenancyAllowed: true,
    editor: {
      type: EditorType.Select,
      optionsSource: 'getProjectLostReason',
      icon: 'info',
    },
    validation: { required: false, valueType: ValueType.IdRef },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Project',
    attribute: 'warrantyExpirationDate',
    label: 'Warranty Expiration',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Date, icon: 'calendar' },
    validation: { required: false, valueType: ValueType.Date },
    execution: {
      writeStrategy: WriteStrategy.SimpleUpdate,
      concurrency: Concurrency.Parallel,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.ValueEquality },
  },
  {
    entityName: 'Conversation',
    attribute: 'deletedAt',
    label: 'Delete conversations',
    actionLabel: 'Delete conversations',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'trash-2' },
    validation: {
      required: false,
      valueType: ValueType.String,
      accessRuleKey: ['AccessToDeleteConversations'],
    },
    execution: {
      writeStrategy: WriteStrategy.SoftDelete,
      concurrency: Concurrency.Serial,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: { supported: true, conflictDetection: ConflictDetection.None },
    confirmation: {
      title: 'Delete selected items?',
      getDescription: (selectedCount, totalCount) =>
        `You are about to delete ${selectedCount} of ${totalCount} selected items. Items will be soft-deleted and can be restored via Undo for a limited time.`,
      warning:
        'This is a destructive action. Related data will also be removed until you undo the operation.',
      cancelButtonTitle: 'Cancel',
      confirmButtonTitle: 'Delete',
      confirmButtonColor: 'error',
    },
  },
  // ─── Address (Site) ────────────────────────────────────────────
  {
    entityName: 'Address',
    attribute: 'deletedAt',
    label: 'Delete sites',
    actionLabel: 'Delete sites',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'trash-2' },
    validation: {
      required: false,
      valueType: ValueType.String,
      accessRuleKey: ['AccessToDeleteAddresses'],
    },
    execution: {
      writeStrategy: WriteStrategy.SoftDelete,
      concurrency: Concurrency.Serial,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: {
      supported: true,
      conflictDetection: ConflictDetection.None,
      accessRuleKey: ['AccessToRestoreDeletedAddresses'],
    },
    confirmation: {
      title: 'Delete selected sites?',
      getDescription: (selectedCount, totalCount) =>
        `You are about to delete ${selectedCount} of ${totalCount} selected sites. Deleting a site also removes its related records (projects, files, memberships, summaries, transcriptions, on-call tickets, labels, and client/conversation links). Sites will be soft-deleted and can be restored via Undo for a limited time.`,
      warning:
        'This is a destructive action. Sites with an active subscription are blocked and must be removed in billing first. Undo is available only for a limited time and only for this job.',
      cancelButtonTitle: 'Cancel',
      confirmButtonTitle: 'Delete',
      confirmButtonColor: 'error',
    },
  },
  // ─── Client ────────────────────────────────────────────────────
  {
    entityName: 'Client',
    attribute: 'deletedAt',
    label: 'Delete clients',
    actionLabel: 'Delete clients',
    isMultitenancyAllowed: true,
    editor: { type: EditorType.Text, icon: 'trash-2' },
    validation: {
      required: false,
      valueType: ValueType.String,
      accessRuleKey: ['AccessToDeleteClients'],
    },
    execution: {
      writeStrategy: WriteStrategy.SoftDelete,
      concurrency: Concurrency.Serial,
    },
    sideEffects: { iterableSync: false, rteInvalidation: true },
    undo: {
      supported: true,
      conflictDetection: ConflictDetection.None,
      accessRuleKey: ['AccessToRestoreClients'],
    },
    confirmation: {
      title: 'Delete selected clients?',
      getDescription: (selectedCount, totalCount) =>
        `You are about to delete ${selectedCount} of ${totalCount} selected clients. Deleting a client also removes its related records (contact emails, phone numbers, site links, and labels). Clients will be soft-deleted and can be restored via Undo for a limited time.`,
      warning:
        'This is a destructive action. Undo is available only for a limited time and only for this job.',
      cancelButtonTitle: 'Cancel',
      confirmButtonTitle: 'Delete',
      confirmButtonColor: 'error',
    },
  },
];

const ENTITY_ACTION_LABEL: Partial<
  Record<BulkUpdateAttributeDefinition['entityName'], string>
> = {
  Conversation: 'conversation',
  Address: 'site',
  Client: 'client',
  Project: 'project',
};

function withActionLabel(def: BulkUpdateAttributeDefinition): BulkUpdateAttributeDefinition {
  if (def.actionLabel) {
    return def;
  }

  const entityLabel = ENTITY_ACTION_LABEL[def.entityName] ?? def.entityName.toLowerCase();
  const normalizedLabel = def.label.toLowerCase();
  const actionLabel = normalizedLabel.startsWith(`${entityLabel} `)
    ? `Update ${normalizedLabel}`
    : `Update ${entityLabel} ${normalizedLabel}`;

  return {
    ...def,
    actionLabel,
  };
}

export const getBulkUpdateRegistry = (): BulkUpdateAttributeDefinition[] =>
  BULK_UPDATE_REGISTRY.map(withActionLabel);
